import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const espnSteps = [
  <>
    Log into your ESPN fantasy football account at{' '}
    <a
      href="https://www.espn.com/fantasy/football/"
      target="_blank"
      rel="noreferrer"
      className="text-green font-semibold hover:text-mustard transition-colors inline-flex items-center gap-0.5"
    >
      espn.com/fantasy/football
      <ExternalLink className="w-3 h-3" />
    </a>.
  </>,
  <>
    Right-click anywhere on the page (Chrome only) and click <em className="font-semibold not-italic text-ds-text">Inspect</em>.
  </>,
  <>
    In the DevTools panel, click the <em className="font-semibold not-italic text-ds-text">Application</em> tab at the top
    (you may need to click the <em className="font-semibold not-italic text-ds-text">»</em> overflow arrow to find it).
  </>,
  <>
    In the left sidebar, expand <em className="font-semibold not-italic text-ds-text">Storage → Cookies → http://fantasy.espn.com</em>.
  </>,
  <>
    Find <strong className="text-ds-text">SWID</strong> in the table on the right. Copy the value from the <em className="font-semibold not-italic">Value</em> column — without the curly brackets.
    It looks like:{' '}
    <code className="bg-surface border border-border rounded px-1.5 py-0.5 text-[0.72rem] font-mono text-text-mid">
      43B70875-0C4B-428L-B608-759A4BB28FA1
    </code>
  </>,
  <>
    Keep scrolling to find <strong className="text-ds-text">espn_s2</strong>. Copy its value — it's much longer and has no curly brackets.
  </>,
];

const sleeperSteps = [
  <>
    Open the{' '}
    <a
      href="https://sleeper.com/fantasy-football"
      target="_blank"
      rel="noreferrer"
      className="text-green font-semibold hover:text-mustard transition-colors inline-flex items-center gap-0.5"
    >
      Sleeper app
      <ExternalLink className="w-3 h-3" />
    </a>{' '}
    or website and navigate to your fantasy football league.
  </>,
  <>
    In the browser address bar the URL will look like:{' '}
    <code className="bg-surface border border-border rounded px-1.5 py-0.5 text-[0.72rem] font-mono text-text-mid">
      sleeper.com/leagues/1234567890123456789/team
    </code>
  </>,
  <>
    The long number in the URL is your <strong className="text-ds-text">League ID</strong>. Copy it and paste it in the field above.
  </>,
];

const CookiesInstructionsBox = ({ provider = 'espn' }) => {
  const [open, setOpen] = useState(false);

  if (provider !== 'espn' && provider !== 'sleeper') return null;

  const isSleeper = provider === 'sleeper';
  const steps = isSleeper ? sleeperSteps : espnSteps;
  const label = isSleeper
    ? 'Where do I find my Sleeper League ID?'
    : 'Where do I find my SWID and espn_s2?';

  return (
    <div className="mt-4 border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-mustard-light transition-colors text-left"
      >
        <div className="flex items-center gap-2 text-text-muted">
          <HelpCircle className="w-4 h-4 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider">
            {label}
          </span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-text-muted shrink-0" />
          : <ChevronDown className="w-4 h-4 text-text-muted shrink-0" />
        }
      </button>

      {open && (
        <ol className="px-4 py-4 bg-ds-white space-y-3 list-none">
          {steps.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full bg-forest text-mustard text-[0.65rem] font-extrabold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="text-xs text-text-mid leading-relaxed">
                {step}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default CookiesInstructionsBox;
